import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MagnifyingGlass, CaretLeft, CaretRight, ClockCounterClockwise, UploadSimple, ArrowsClockwise } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';

interface StatusRow {
  id: number;
  inf3: string | null;
  aid: string | null;
  vnm: string | null;
  nnm: string | null;
  plz: string | null;
  str: string | null;
  ort: string | null;
  email_address: string | null;
  tel_optin: number;
  tel_optin_ts: string | null;
  tel_optout_ts: string | null;
  post_optin: number;
  post_optin_ts: string | null;
  post_optout_ts: string | null;
  email_optin: number;
  email_optin_ts: string | null;
  email_optout_ts: string | null;
}

interface HistoryEntry {
  source: 'cache' | 'bestand' | 'newsletter' | 'optout';
  id: number;
  contact_id: string | null;
  datum: string | null;
  tel: string | null;
  email: string | null;
  post: string | null;
  fc_user: string | null;
  user_tenant: string | null;
  fc_status: string | null;
  fc_status_detail: string | null;
  recording_url: string | null;
  wkz: string | null;
  kampagne_typ: string | null;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return d; }
}

function fmtShort(d: string | null): string {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
  catch { return d; }
}

function OptinBadge({ optin, optinTs, optoutTs }: { optin: number; optinTs: string | null; optoutTs: string | null }) {
  if (optin === 1) {
    return (
      <span style={{ display: 'inline-block', background: '#059669', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600 }}
        title={`Ja seit ${fmtDate(optinTs)}${optoutTs ? `\nDavor Nein seit ${fmtDate(optoutTs)}` : ''}`}>
        Ja {fmtShort(optinTs) && <span style={{ fontWeight: 400, opacity: 0.85 }}>seit {fmtShort(optinTs)}</span>}
      </span>
    );
  }
  if (optin === 0 && (optinTs || optoutTs)) {
    return (
      <span style={{ display: 'inline-block', background: '#dc2626', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600 }}
        title={`Nein seit ${fmtDate(optoutTs)}${optinTs ? `\nDavor Ja seit ${fmtDate(optinTs)}` : ''}`}>
        Nein {fmtShort(optoutTs) && <span style={{ fontWeight: 400, opacity: 0.85 }}>seit {fmtShort(optoutTs)}</span>}
      </span>
    );
  }
  return <span style={{ color: '#9ca3af', fontSize: '0.72rem' }}>—</span>;
}

const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600,
  color: '#6b7280', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: '0.8rem', borderBottom: '1px solid #f3f4f6',
};

export default function OptinStatus() {
  const [records, setRecords] = useState<StatusRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  // History Dialog
  const [historyInf3, setHistoryInf3] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<{ status: StatusRow | null; history: HistoryEntry[] } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Upload Dialog
  const [showUpload, setShowUpload] = useState(false);
  const [uploadType, setUploadType] = useState<'S023' | 'S012'>('S023');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string } | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (appliedSearch) params.set('search', appliedSearch);
      const res = await authFetch(getApiUrl(`/optin-cache/status?${params}`));
      const data = await res.json();
      setRecords(data.records || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  }, [page, appliedSearch]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleSearch = () => { setAppliedSearch(search); setPage(1); };
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openHistory = async (key: string, type: 'inf3' | 'email' = 'inf3') => {
    setHistoryInf3(key);
    setHistoryLoading(true);
    try {
      const param = type === 'email' ? `?email=${encodeURIComponent(key)}` : '';
      const path = type === 'email' ? `/optin-cache/status/_by-email/history${param}` : `/optin-cache/status/${encodeURIComponent(key)}/history`;
      const res = await authFetch(getApiUrl(path));
      const data = await res.json();
      setHistoryData(data);
    } catch (err) {
      console.error('History error:', err);
    }
    setHistoryLoading(false);
  };

  const closeHistory = () => { setHistoryInf3(null); setHistoryData(null); setExpandedRows(new Set()); };

  const toggleExpand = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_type', uploadType);
      const res = await authFetch(getApiUrl('/optin-cache/status/upload'), {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setUploadResult({ success: true, message: `${data.rowCount.toLocaleString('de-DE')} Eintraege importiert (${data.sourceType})` });
      } else {
        setUploadResult({ success: false, message: data.error || 'Fehler' });
      }
    } catch (err) {
      setUploadResult({ success: false, message: (err as Error).message });
    }
    setUploading(false);
  };

  const handleRebuild = async () => {
    if (!window.confirm('optin_status komplett neu aufbauen? Das dauert ca. 7 Minuten.')) return;
    setRebuilding(true);
    try {
      const res = await authFetch(getApiUrl('/optin-cache/status/rebuild'), { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert(`Rebuild fertig: ${data.total.toLocaleString('de-DE')} Eintraege`);
        loadRecords();
      } else {
        alert(`Rebuild fehlgeschlagen: ${data.error}`);
      }
    } catch (err) {
      alert(`Fehler: ${(err as Error).message}`);
    }
    setRebuilding(false);
  };

  return (
    <div>
      {/* Suche + Aktionen */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
        <MagnifyingGlass size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Name, PLZ, Ort, Straße, Email, inf3, AID..."
          style={{
            width: '350px', flexShrink: 0, padding: '8px 12px', border: '1px solid #d1d5db',
            borderRadius: '6px', fontSize: '0.85rem',
          }}
        />
        <button onClick={handleSearch}
          style={{ padding: '8px 16px', background: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', flexShrink: 0 }}>
          Suchen
        </button>
        <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
          {total.toLocaleString('de-DE')} Ergebnisse
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => { setShowUpload(true); setUploadResult(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
            <UploadSimple size={14} /> Import
          </button>
          <button onClick={handleRebuild} disabled={rebuilding}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: rebuilding ? 'wait' : 'pointer', fontSize: '0.8rem', opacity: rebuilding ? 0.6 : 1 }}>
            <ArrowsClockwise size={14} /> {rebuilding ? 'Rebuild...' : 'Rebuild'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.82rem', color: '#6b7280' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}><CaretLeft size={14} /></button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}><CaretRight size={14} /></button>
        </div>
      </div>

      {/* Tabelle */}
      <div style={{ overflowX: 'auto', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', opacity: loading ? 0.5 : 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>PLZ</th>
              <th style={thStyle}>Ort</th>
              <th style={thStyle}>Straße</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Tel</th>
              <th style={thStyle}>Post</th>
              <th style={thStyle}>Email-Optin</th>
              <th style={thStyle}>AID</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {records.map(r => (
              <tr key={r.id}>
                <td style={tdStyle}>
                  <strong>{r.nnm}</strong>{r.vnm ? `, ${r.vnm}` : ''}
                </td>
                <td style={tdStyle}>{r.plz || ''}</td>
                <td style={tdStyle}>{r.ort || ''}</td>
                <td style={tdStyle}>{r.str || ''}</td>
                <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#6b7280' }}>{r.email_address || ''}</td>
                <td style={tdStyle}><OptinBadge optin={r.tel_optin} optinTs={r.tel_optin_ts} optoutTs={r.tel_optout_ts} /></td>
                <td style={tdStyle}><OptinBadge optin={r.post_optin} optinTs={r.post_optin_ts} optoutTs={r.post_optout_ts} /></td>
                <td style={tdStyle}><OptinBadge optin={r.email_optin} optinTs={r.email_optin_ts} optoutTs={r.email_optout_ts} /></td>
                <td style={{ ...tdStyle, fontSize: '0.75rem', color: '#9ca3af' }}>{r.aid || ''}</td>
                <td style={tdStyle}>
                  {(r.inf3 || r.email_address) && (
                    <button onClick={e => { e.stopPropagation(); r.inf3 ? openHistory(r.inf3) : openHistory(r.email_address!, 'email'); }}
                      style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', padding: '3px 6px', cursor: 'pointer', color: '#6b7280' }}
                      title="History">
                      <ClockCounterClockwise size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {records.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                Keine Ergebnisse
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* History Dialog */}
      {historyInf3 && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '5vh',
        }} onClick={closeHistory}>
          <div style={{
            background: 'white', borderRadius: '12px', width: '95%', maxWidth: '1100px',
            maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: '#003366', color: 'white', padding: '16px 24px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>
                  History: {historyData?.status?.vnm} {historyData?.status?.nnm}
                </h3>
                <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                  {historyData?.status?.plz} {historyData?.status?.ort} · inf3: {historyInf3}
                </span>
              </div>
              <button onClick={closeHistory} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>

            {historyLoading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Lade...</div>
            ) : historyData ? (
              <div style={{ padding: '20px' }}>
                {/* Aktueller Status */}
                {historyData.status && (
                  <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div>
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>Telefon</span>
                      <OptinBadge optin={historyData.status.tel_optin} optinTs={historyData.status.tel_optin_ts} optoutTs={historyData.status.tel_optout_ts} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>Post</span>
                      <OptinBadge optin={historyData.status.post_optin} optinTs={historyData.status.post_optin_ts} optoutTs={historyData.status.post_optout_ts} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>Email</span>
                      <OptinBadge optin={historyData.status.email_optin} optinTs={historyData.status.email_optin_ts} optoutTs={historyData.status.email_optout_ts} />
                    </div>
                    {historyData.status.email_address && (
                      <div>
                        <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>Email-Adresse</span>
                        <span style={{ fontSize: '0.82rem' }}>{historyData.status.email_address}</span>
                      </div>
                    )}
                    {historyData.status.aid && (
                      <div>
                        <span style={{ fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>AID</span>
                        <span style={{ fontSize: '0.82rem' }}>{historyData.status.aid}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* History Tabelle */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={thStyle}>Quelle</th>
                      <th style={thStyle}>Datum</th>
                      <th style={thStyle}>Optin</th>
                      <th style={thStyle}>Tel</th>
                      <th style={thStyle}>Post</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Telefon</th>
                      <th style={thStyle}>Agent / CC</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>WKZ</th>
                      <th style={thStyle}>Kampagne</th>
                      <th style={thStyle}>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.history.map((h: any, i) => {
                      const rowKey = `${h.source}-${h.id}-${i}`;
                      const isExpanded = expandedRows.has(rowKey);
                      const hasDetails = h.source === 'cache' || h.source === 'bestand' || h.source === 'newsletter';
                      const detailFields = [
                        ['Contact-ID', h.contact_id],
                        ['HKN', h.hkn],
                        ['Herkunft', h.herkunft],
                        ['FBD', h.fbd],
                        ['FBR', h.fbr],
                        ['FC Status', h.fc_status],
                        ['FC Datum', h.fc_date ? fmtDate(h.fc_date) : null],
                        ['CC', h.user_tenant],
                      ].filter(([, v]) => v !== null && v !== undefined && v !== '');
                      return (
                      <React.Fragment key={rowKey}>
                      <tr style={{ borderBottom: '1px solid #f3f4f6', cursor: hasDetails ? 'pointer' : 'default', background: isExpanded ? '#f0f9ff' : undefined }}
                          onClick={() => hasDetails && toggleExpand(rowKey)}>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                            background: h.source === 'cache' ? '#dbeafe' : h.source === 'newsletter' ? '#d1fae5' : h.source === 'optout' ? '#fce7f3' : '#fef3c7',
                            color: h.source === 'cache' ? '#1e40af' : h.source === 'newsletter' ? '#065f46' : h.source === 'optout' ? '#9d174d' : '#92400e',
                          }}>
                            {h.source === 'cache' ? 'Cache' : h.source === 'newsletter' ? 'Newsletter' : h.source === 'optout' ? 'Optout' : 'Bestand'}
                          </span>
                          {hasDetails && <span style={{ fontSize: '0.65rem', marginLeft: '4px', color: '#9ca3af' }}>{isExpanded ? '▾' : '▸'}</span>}
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(h.datum)}</td>
                        <td style={tdStyle}>
                          {h.optin === 'Ja' ? <span style={{ color: '#059669', fontWeight: 600 }}>Ja</span> :
                           h.optin === 'Nein' ? <span style={{ color: '#dc2626' }}>Nein</span> : '—'}
                        </td>
                        <td style={tdStyle}>
                          {h.tel === 'Ja' ? <span style={{ color: '#059669', fontWeight: 600 }}>Ja</span> :
                           h.tel === 'Nein' ? <span style={{ color: '#dc2626' }}>Nein</span> : '—'}
                        </td>
                        <td style={tdStyle}>
                          {h.post === 'Ja' ? <span style={{ color: '#059669', fontWeight: 600 }}>Ja</span> :
                           h.post === 'Nein' ? <span style={{ color: '#dc2626' }}>Nein</span> : '—'}
                        </td>
                        <td style={tdStyle}>
                          {h.email === 'Ja' ? <span style={{ color: '#059669', fontWeight: 600 }}>Ja</span> :
                           h.email === 'Nein' ? <span style={{ color: '#dc2626' }}>Nein</span> : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{h.telefon || '—'}</td>
                        <td style={{ ...tdStyle, fontSize: '0.72rem' }}>
                          {h.fc_user || '—'}
                          {h.user_tenant && <span style={{ color: '#9ca3af' }}> ({h.user_tenant})</span>}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '0.72rem' }}>
                          {h.fc_status_detail || h.fc_status || '—'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{h.wkz || ''}</td>
                        <td style={{ ...tdStyle, fontSize: '0.72rem' }}>{h.kampagne_typ || ''}</td>
                        <td style={tdStyle}>
                          {h.recording_url && (
                            <a href={h.recording_url} target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ color: '#2563eb', fontSize: '0.7rem' }}>▶</a>
                          )}
                        </td>
                      </tr>
                      {isExpanded && detailFields.length > 0 && (
                        <tr>
                          <td colSpan={12} style={{ padding: '8px 16px 12px 32px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px 24px', fontSize: '0.75rem' }}>
                              {detailFields.map(([label, val]) => (
                                <div key={label as string} style={{ display: 'flex', gap: '6px' }}>
                                  <span style={{ color: '#6b7280', minWidth: '90px' }}>{label}</span>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{String(val)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                    {historyData.history.length === 0 && (
                      <tr><td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>
                        Keine History-Einträge
                      </td></tr>
                    )}
                  </tbody>
                </table>
                <div style={{ marginTop: '8px', fontSize: '0.72rem', color: '#9ca3af' }}>
                  {historyData.history.length} Eintraege
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {/* Upload Dialog */}
      {showUpload && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '15vh',
        }} onClick={() => setShowUpload(false)}>
          <div style={{
            background: 'white', borderRadius: '12px', width: '480px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#003366', color: 'white', padding: '16px 24px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Optin/Optout Import</h3>
              <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>x</button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Typ</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['S023', 'S012'] as const).map(t => (
                    <button key={t} onClick={() => setUploadType(t)}
                      style={{
                        padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                        border: uploadType === t ? '2px solid #003366' : '1px solid #d1d5db',
                        background: uploadType === t ? '#eff6ff' : 'white',
                        color: uploadType === t ? '#003366' : '#6b7280',
                      }}>
                      {t === 'S023' ? 'S023 Newsletter-Optins' : 'S012 Optouts'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>
                  {uploadType === 'S023' ? 'XLS/XLSX mit Newsletter-Optins (xpecto-Format)' : 'XLS/XLSX mit E-Mail, Grund, Datum'}
                </label>
                <input ref={fileRef} type="file" accept=".xls,.xlsx"
                  style={{ fontSize: '0.85rem' }} />
              </div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '16px', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px' }}>
                {uploadType === 'S023'
                  ? 'Erwartet: xpecto-Export mit VNM, NNM, PLZ, STR, EMA, OPTINTEL, OPTINEMA, OPTINMLG, OPTINAB. Inf3 wird automatisch berechnet wo Adresse vorhanden.'
                  : 'Erwartet: 3 Spalten — E-Mail, Grund, Datum (DD.MM.YYYY HH:MM:SS). Setzt email_optin auf Nein fuer gematchte Adressen.'}
              </div>
              {uploadResult && (
                <div style={{
                  padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.85rem',
                  background: uploadResult.success ? '#d1fae5' : '#fee2e2',
                  color: uploadResult.success ? '#065f46' : '#991b1b',
                }}>
                  {uploadResult.message}
                  {uploadResult.success && (
                    <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#6b7280' }}>
                      Jetzt "Rebuild" klicken um optin_status zu aktualisieren.
                    </div>
                  )}
                </div>
              )}
              <button onClick={handleUpload} disabled={uploading}
                style={{
                  padding: '10px 20px', background: '#059669', color: 'white', border: 'none',
                  borderRadius: '6px', cursor: uploading ? 'wait' : 'pointer', fontSize: '0.85rem',
                  fontWeight: 600, opacity: uploading ? 0.6 : 1,
                }}>
                {uploading ? 'Importiere...' : 'Importieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
